<template>
  <div class="modal-repositories-item" :class="mainClasses">
    <div class="modal-repositories-item__info">
      <div class="modal-repositories-item__name">{{ item.name }}</div>
      <div class="modal-repositories-item__owner">
        owned by
        <span class="modal-repositories-item__owner-name">
          {{ displayOwner }}
        </span>
      </div>
    </div>
    <div v-if="hasCollaborators" class="modal-repositories-item__colaboration">
      <div
        v-tooltip.top="`${item.collaborators_count} collaborators`"
        class="modal-repositories-item__collaborators"
      >
        <UserGroupIcon
          aria-hidden="true"
          class="modal-repositories-item__shared-icon"
        />
        {{ item.collaborators_count }}
      </div>
      <div class="modal-repositories-item__permission">
        {{ displayPermission }}
      </div>
    </div>
    <RepositoriesItemMenu ref="menuRef" :items="menuItems" size="xs" />
  </div>
</template>

<script lang="ts" setup>
import { computed, markRaw, ref, useTemplateRef } from 'vue'

import { DottedMenuItem, Repository } from '@types'

import { ACCESS_TYPES, ACCESS_TYPE_READONLY } from '@/const'

import { useModalsStore } from '@/store/modals'
import { useUserStore } from '@/store/user'

import RepositoryPopup from '@/modules/modals/Repositories/Data/RepositoryPopup.vue'
import {
  UserGroupIcon,
  EyeIcon,
  ArrowUpTrayIcon,
  PencilIcon,
  TrashIcon,
} from '@heroicons/vue/24/outline'
import RepositoriesItemMenu from './RepositoriesItemMenu.vue'

type Props = {
  item: Repository
  active?: boolean
}

type Emits = {
  delete: [item: Repository]
  unlink: [item: Repository]
  export: [id: string, name: string, resolve: () => void, reject: () => void]
  close: []
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

const modalsStore = useModalsStore()
const userStore = useUserStore()

const menuRef = useTemplateRef('menuRef')

const exporting = ref(false)

const hasCollaborators = computed(() => props.item.collaborators_count > 1)

const isOwner = computed(() => props.item.owner === userStore.getEmail)

const displayOwner = computed(() => {
  return isOwner.value ? 'me' : props.item.owner
})

const displayPermission = computed(() => {
  if (props.item.settings.readonly) return 'readonly'
  return (
    props.item.user_repo_settings?.access_type &&
    ACCESS_TYPES[props.item.user_repo_settings.access_type - 1]
  )
})

const isReadonly = computed(
  () => props.item?.user_repo_settings?.access_type === ACCESS_TYPE_READONLY,
)

const menuItems = computed<DottedMenuItem[]>(() => {
  if (isReadonly.value) {
    const items: DottedMenuItem[] = [
      {
        label: 'View',
        leading: EyeIcon,
        variant: 'secondary',
        fill: 'light',
        action: handleClickEdit,
      },
    ]

    if (!isOwner.value) {
      items.push({
        label: 'Unlink',
        leading: TrashIcon,
        variant: 'danger',
        fill: 'light',
        action: handleClickUnlink,
      })
    }

    return items
  }

  return [
    {
      label: 'Export',
      leading: ArrowUpTrayIcon,
      variant: 'gray',
      fill: 'light',
      loading: exporting.value,
      disabled: exporting.value,
      hideLabelToSm: true,
      action: handleClickExport,
    },
    {
      label: 'Edit',
      leading: PencilIcon,
      variant: 'secondary',
      fill: 'light',
      action: handleClickEdit,
    },
    {
      label: isOwner.value ? 'Delete' : 'Unlink',
      leading: TrashIcon,
      variant: 'danger',
      fill: 'light',
      action: isOwner.value ? handleClickDelete : handleClickUnlink,
    },
  ]
})

const mainClasses = computed(() => ({
  'modal-repositories-item--active': props.active,
}))

const handleClickEdit = async () => {
  const modalInstance = modalsStore.init(
    `repository-${props.item.id}`,
    markRaw(RepositoryPopup),
  )

  modalInstance?.open(modalsStore.getZIndex(), {
    repository: props.item,
    returnable: true,
  })
  emit('close')
}

const handleClickDelete = () => {
  menuRef.value?.hide()
  emit('delete', props.item)
}

const handleClickUnlink = () => {
  menuRef.value?.hide()
  emit('unlink', props.item)
}

const handleClickExport = () => {
  exporting.value = true
  emit(
    'export',
    props.item.id,
    props.item.name,
    () => {
      exporting.value = false
    },
    () => {
      exporting.value = false
    },
  )
}
</script>

<style>
.modal-repositories-item {
  @apply flex items-center justify-between;
  @apply gap-1.5 py-2 pr-2 pl-[calc(.625rem-1px)];
  @apply rounded;
  @apply bg-inherit hover:bg-body-gray;
  @apply text-gray-400 dark:text-gray-400;
  @apply cursor-pointer;

  .ui-dotted-menu__area {
    @apply -inset-y-1;
    @apply text-body-dark dark:text-body;
  }

  &:hover .ui-dotted-menu__area {
    @apply text-body-gray dark:text-body-gray;
  }

  &--active {
    @apply !bg-indigo-50 dark:!bg-gray-900;
    @apply outline outline-1 outline-indigo-100 dark:outline-gray-800;

    .ui-dotted-menu__area {
      @apply !text-indigo-50 dark:!text-gray-900;
    }
  }

  &__info {
    @apply flex flex-col flex-auto;
    @apply gap-1;
    @apply overflow-hidden;
  }

  &__name {
    @apply line-clamp-2;
    @apply text-base sm:text-sm;
    @apply font-medium;
    @apply text-light-text dark:text-gray-300;
  }

  &__owner {
    @apply flex items-center;
    @apply gap-x-1;
    @apply text-sm sm:text-xs;
    @apply whitespace-nowrap;
  }

  &__owner-name {
    @apply font-medium;
    @apply truncate;
  }

  &--active &__name {
    @apply text-primary dark:text-indigo-400;
  }

  &__shared-icon {
    @apply w-4 h-4;
  }

  &__colaboration {
    @apply flex flex-col items-end shrink-0;
    @apply gap-1 pt-1;
    @apply text-sm sm:text-xs;
  }

  &__collaborators {
    @apply flex items-center;
    @apply gap-x-1;
    @apply text-indigo-400;
  }

  &__permission {
    @apply text-xs sm:text-[0.6875rem];
    @apply text-gray-400 dark:text-gray-500;
  }
}
</style>
